import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";

import { appsSdksSignalChangeTypeMap } from "app/pages/sales-intelligence/domain/signals/types/apps-signal";
import {
    getAppsSdksSignalEventActionText,
    getAppsSdksSignalEventInstalledForDays,
} from "app/pages/sales-intelligence/modules/company-analysis/services/signals/getters/getAppsSdksSignalEventText";
import { getSignalEventPeriod } from "app/pages/sales-intelligence/modules/company-analysis/services/signals/getters/getSignalEventDate";
import type { AppsSdksSignalEventDto } from "app/pages/sales-intelligence/modules/company-analysis/types/signals";
import TruncatedTextWithTooltip from "app/pages/sales-intelligence/pages/company-analysis/components/shared/TruncatedTextWithTooltip/TruncatedTextWithTooltip";
import signalsI18nKeys from "app/pages/sales-intelligence/sub-modules/signals/utils/i18n";

type AppsSignalEventInfoBoxProps = {
    signal: AppsSdksSignalEventDto;
    companyName: string;
    "data-automation"?: string;
};

type Translate = ReturnType<typeof useTranslate>["translate"];

const getAppsSignalEventInfoBoxText = (
    signal: AppsSdksSignalEventDto,
    companyName: string,
    translate: Translate,
): string => {
    const { sdks, changeType } = signal;
    const installedForDays = getAppsSdksSignalEventInstalledForDays(signal);

    if (sdks.length === 1) {
        return installedForDays
            ? translate(signalsI18nKeys.apps.companyOverview.installedFor, {
                  sdk: sdks[0],
                  period: getSignalEventPeriod(installedForDays),
              })
            : "";
    }

    return changeType === appsSdksSignalChangeTypeMap.renewalComingUp
        ? ""
        : `${getAppsSdksSignalEventActionText(signal, companyName)} ${sdks.join(", ")}`;
};

const AppsSignalEventInfoBox = ({
    signal,
    companyName,
    "data-automation": dataAutomation,
}: AppsSignalEventInfoBoxProps) => {
    const { translate } = useTranslate();
    const text = getAppsSignalEventInfoBoxText(signal, companyName, translate);

    if (!text) {
        return null;
    }

    return (
        <TruncatedTextWithTooltip
            data-automation={dataAutomation}
            height={32}
            text={text}
            textStyles={{
                fontVariant: "input2",
                color: colorsPalettes.carbon[300],
            }}
        />
    );
};

export default AppsSignalEventInfoBox;
